export { urlFragment } from "utils/request";

/**
 * Returns a React route to a staff user's profile page.
 *
 * If given no arguments, returns the route with placeholders, suitable for use
 * to define the route.
 *
 * @param {Object} staffUser - the staff user of interest
 * @param {number|string} staffUser.staffUserId - the ID of the staff user
 * @returns {string} A route suitable for use with the React router.
 */
export const toStaffUser = ({ staffUserId } = { staffUserId: ":id" }) =>
  `/connection/${staffUserId}`;

/**
 * Returns [forProvider] suffixed with "/schedule" to represent the
 * appointment-scheduling link for a university service provider.
 *
 * @param {string} forProvider - the route to a university service provider
 * @returns {string} the scheduling route for the provider
 */
const schedule = (forProvider) => `${forProvider}/schedule`;

/**
 * Returns a React route to schedule a direct appointment with a staff user
 * (viz. not tied to a university service).
 *
 * If given no arguments, returns the route with placeholders, suitable for use
 * to define the route.
 *
 * @param {Object} staffUser - the staff user to schedule the appointment with
 * @param {number|string} staffUser.staffUserId - the ID of the staff user
 * @returns {string} A route suitable for use with the React router.
 */
export const toScheduleStaffUserAppointment = (staffUser = { staffUserId: ":id" }) =>
  schedule(toStaffUser(staffUser));

/**
 * Returns a React route to view a staff user who's a member of an institutional
 * service.
 *
 * If given no serviceId, returns the route to view the staff user's profile,
 * independent of any university services.
 *
 * If given no arguments, returns the route with placeholders, suitable for use
 * to define the route.
 *
 * @param {Object} serviceStaffUser - the staff user + service
 * @param {number|string} serviceStaffUser.staffUserId - the ID of the staff user
 * @param {number|string} serviceStaffUser.serviceId - the ID of the institutional service
 * @returns {string} A route suitable for use with the React router.
 */
export const toStaffUserInService = ({ staffUserId, serviceId } = { staffUserId: ":id", serviceId: ":serviceId" }) =>
  (!serviceId
    ? toStaffUser({ staffUserId })
    : `/connection/${staffUserId}/service/${serviceId}`);

/**
 * Returns a React route to schedule an appointment with a staff user for
 * a specific institutional service.
 *
 * If given no serviceId, returns the route to schedule an appointment with
 * the staff user directly (viz. not within an institutional service).
 *
 * If given no arguments, returns the route with placeholders, suitable for use
 * to define the route.
 *
 * @param {Object} serviceStaffUser - the staff user + service to schedule the appointment with
 * @param {number|string} serviceStaffUser.staffUserId - the ID of the staff user
 * @param {number|string} serviceStaffUser.serviceId - the ID of the institutional service
 * @returns {string} A route suitable for use with the React router.
 */
export const toScheduleStaffUserAppointmentForService = (serviceStaffUser = { staffUserId: ":id", serviceId: ":serviceId" }) =>
  schedule(toStaffUserInService(serviceStaffUser));
